import { PREFIX_RE, langFromPath, OG_LOCALES, SUPPORTED } from "@/lib/i18n";
import { siteName, authorName } from "@/lib/siteIdentity";
// Restores the homepage share metadata (defined statically in index.html)
// after SPA navigation away from an article page.
const SITE = () => siteName("ar");
const DESC = "تغطية إخبارية موثوقة لأخبار غزة وفلسطين والعالم، أخبار عاجلة ومقاطع فيديو من قلب الحدث.";
const HOME = "https://alisalemps.com/";
const HOME_EN = "https://alisalemps.com/en/";
const IMG = "https://alisalemps.com/api/public/og-image.png";
const SITE_EN = () => siteName("en");
const DESC_EN = "Trusted news coverage of Gaza, Palestine and the world — breaking news and video from the heart of the story.";

function setAlternates(pairs) {
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    for (const [hl, href] of pairs) { const l = document.createElement("link"); l.rel = "alternate"; l.hreflang = hl; l.href = href; document.head.appendChild(l); }
}

function set(attr, key, value) {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", value);
}

export function resetHomeMeta(lang = "ar") {
    const en = lang !== "ar";
    const site = en ? SITE_EN() : SITE(), desc = en ? DESC_EN : DESC, home = en ? `${HOME}${lang}/` : HOME;
    set("name", "description", `${site} | ${desc}`);
    set("property", "og:type", "website");
    set("property", "og:title", site);
    set("property", "og:description", desc);
    set("property", "og:url", home);
    set("property", "og:image", IMG);
    set("property", "og:locale", OG_LOCALES[lang] || "ar_AR");
    set("property", "og:locale:alternate", en ? "ar_AR" : "en_US");
    set("property", "og:site_name", site);
    set("name", "twitter:card", "summary_large_image");
    set("name", "twitter:title", site);
    set("name", "twitter:description", desc);
    set("name", "twitter:image", IMG);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", home);   // each language home is canonical for itself
    setAlternates([...SUPPORTED.map((l) => [l, l === "ar" ? HOME : `${HOME}${l}/`]), ["x-default", HOME]]);
    const r = document.querySelector('meta[name="robots"]');
    if (r) r.remove();
    const ld = document.getElementById("ld-newsarticle"); if (ld) ld.remove();
    setSiteGraph(lang, site, desc, home);
    dedupeHead();
}

// WebSite + Person (publisher/author) graph for the home page — stable @ids so re-renders never duplicate entities.
function setSiteGraph(lang, site, desc, home) {
    const root = HOME.replace(/\/$/, "");
    const en = lang !== "ar";
    const graph = {
        "@context": "https://schema.org",
        "@graph": [
            { "@type": "WebSite", "@id": `${root}/#website`, "url": home, "name": site, "description": desc, "inLanguage": lang,
              "publisher": { "@id": `${root}/#person` },
              "potentialAction": { "@type": "SearchAction", "target": `${root}${en ? `/${lang}` : ""}/search?q={search_term_string}`, "query-input": "required name=search_term_string" } },
            { "@type": "Person", "@id": `${root}/#person`, "name": authorName(lang), "jobTitle": en ? "Journalist" : "صحفي", "url": `${root}${en ? `/${lang}` : ""}/about`,
              "worksFor": { "@type": "Organization", "@id": `${root}/#organization`, "name": site, "url": home, "logo": IMG } },
        ],
    };
    let el = document.getElementById("ld-website");
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "ld-website"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(graph);
}

export function removeSiteGraph() { const el = document.getElementById("ld-website"); if (el) el.remove(); }

// Secondary pages (sections, search, about…): canonical = own URL in the current language, hreflang pair.
export function setPageMeta(pathname, search = "") {
    const bare = pathname.replace(PREFIX_RE, "") || "/";
    const page = parseInt(new URLSearchParams(search).get("page") || "1", 10) || 1;
    const q = page > 1 ? `?page=${page}` : "";   // archive pages (?page=2…) are canonical for themselves
    const root = HOME.replace(/\/$/, "");
    const urlFor = (l) => root + (l === "ar" ? bare : `/${l}` + (bare === "/" ? "/" : bare)) + q;
    const cur = langFromPath(pathname);
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", urlFor(cur));
    set("property", "og:url", urlFor(cur));
    set("property", "og:locale", OG_LOCALES[cur]);
    set("property", "og:site_name", cur === "ar" ? SITE() : SITE_EN());
    setAlternates([...SUPPORTED.map((l) => [l, urlFor(l)]), ["x-default", urlFor("ar")]]);
    dedupeHead();
}

/** Keep exactly one canonical and one tag per og:/twitter: property (later duplicates are removed). */
export function dedupeHead() {
    document.querySelectorAll('link[rel="canonical"]').forEach((n, i) => { if (i) n.remove(); });
    const seen = new Set();
    document.querySelectorAll("meta[property^='og:'], meta[name^='twitter:'], meta[name='description']").forEach((n) => {
        const k = (n.getAttribute("property") || n.getAttribute("name"));
        if (k === "og:image:width" || k === "og:image:height" || k === "og:locale:alternate") return;
        if (seen.has(k)) n.remove(); else seen.add(k);
    });
}
